import './AnalyticsPage.css';

interface AnalyticsPageViewProps {
  loading: boolean;
  error: string | null;
  dashboard: any | null;
  payments: any | null;
  subscriptions: any | null;
  revenue: any | null;
  apiKeyMissing?: boolean;
}

// Format a raw value into a human-readable string; never returns [object Object]
function fmtValue(key: string, value: any): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') {
    const k = key.toLowerCase();
    if (k.includes('rate') || k.includes('percent')) return `${(value * 100).toFixed(1)}%`; // rates are 0-1 fractions
    if (k.includes('revenue') || k.includes('amount') || k.includes('mrr') || k.includes('lost')) return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    if (k === 'uptime') { // uptime in seconds
      const h = Math.floor(value / 3600);
      const m = Math.floor((value % 3600) / 60);
      return h > 0 ? `${h}h ${m}m` : `${m}m`;
    }
    if (k.includes('ms') || k.includes('processing')) return `${value.toFixed(1)}ms`;
    return value.toLocaleString();
  }
  if (Array.isArray(value)) return value.length === 0 ? '—' : `${value.length} item${value.length !== 1 ? 's' : ''}`;
  if (typeof value === 'object') return JSON.stringify(value); // nested object — stringify rather than [object Object]
  return String(value);
}

// Capitalise and space a camelCase key label
function fmtKey(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, s => s.toUpperCase())
    .trim();
}

// Stat row: label on left, formatted value on right
const StatRow = ({ label, value, raw }: { label: string; value: string; raw?: any }) => (
  <li className="data-display-item">
    <span className="data-display-key">{label}</span>
    <span className={typeof raw === 'number' && raw === 0 ? 'analytics-stat-zero' : 'analytics-stat-value'}>{value}</span>
  </li>
);

// Render a flat object's scalar fields as stat rows, skipping deeply nested objects and arrays
const StatSection = ({ title, obj }: { title: string; obj: any }) => {
  if (!obj || typeof obj !== 'object') return null;
  const entries = Object.entries(obj).filter(([, v]) => typeof v !== 'object' || v === null);
  if (entries.length === 0) return null;
  return (
    <>
      {title && <h3 className="analytics-subsection-header">{title}</h3>}
      <ul className="data-display-list">
        {entries.map(([k, v]) => (
          <StatRow key={k} label={fmtKey(k)} value={fmtValue(k, v)} raw={v} />
        ))}
      </ul>
    </>
  );
};

// Dashboard (KPIs) section — renders each KPI group as a sub-section
const DashboardSection = ({ data }: { data: any }) => {
  if (!data) return <p className="analytics-empty">No data</p>;
  const groups: [string, any][] = Object.entries(data).filter(([k, v]) => k !== 'generatedAt' && typeof v === 'object' && v !== null && !Array.isArray(v));
  return (
    <>
      {groups.map(([groupKey, groupObj]) => (
        <StatSection key={groupKey} title={fmtKey(groupKey)} obj={groupObj} />
      ))}
    </>
  );
};

// Generic analytics section: renders summary + date range
const AnalyticsSection = ({ data }: { data: any }) => {
  if (!data) return <p className="analytics-empty">No data</p>;
  return (
    <>
      {data.summary && <StatSection title="Summary" obj={data.summary} />}
      {data.dateRange && <StatSection title="Date Range" obj={{ from: data.dateRange.startDate?.split('T')[0], to: data.dateRange.endDate?.split('T')[0] }} />}
      {data.creditAnalytics && <StatSection title="Credit Analytics" obj={data.creditAnalytics} />}
    </>
  );
};

function AnalyticsPageView({ loading, error, dashboard, payments, subscriptions, revenue, apiKeyMissing }: AnalyticsPageViewProps) {
  if (loading) return <main className="analytics-loading" role="status" aria-live="polite">Loading analytics…</main>;
  if (error) return <main className="analytics-error" role="alert" aria-live="polite">Error: {error}</main>;

  return (
    <main className="analytics-container">
      {apiKeyMissing && (
        <div className="admin-api-key-warning">
          Analytics routes require an API key. Set VITE_QreditCharge_API_KEY or localStorage.QreditCharge_API_KEY and refresh.
        </div>
      )}
      <h1 className="analytics-header">Analytics</h1>

      <div className="analytics-card">
        <h2 className="analytics-section-header">KPI Dashboard</h2>
        <DashboardSection data={dashboard} />
      </div>

      <div className="analytics-card">
        <h2 className="analytics-section-header">Payments (Last 30 Days)</h2>
        <AnalyticsSection data={payments} />
      </div>

      <div className="analytics-card">
        <h2 className="analytics-section-header">Subscriptions</h2>
        <AnalyticsSection data={subscriptions} />
      </div>

      <div className="analytics-card">
        <h2 className="analytics-section-header">Revenue (Last 30 Days)</h2>
        <AnalyticsSection data={revenue} />
      </div>
    </main>
  );
}

export default AnalyticsPageView;
